import React from "react";
import { Workflow } from "../../../../../services/WorkflowService";
import { Button } from "../../../../ui/button";
import { Badge } from "../../../../ui/badge";
import { PencilIcon, Trash2Icon, ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { ComputedArgBinding, GenerationVariable } from "./types";

function workflowDisplayName(workflows: Workflow[], refName: string): string {
  const wf = workflows.find((w) => w.workflow_ref_name === refName);
  return wf ? wf.workflow_name : refName;
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function summarizeArgBinding(b: ComputedArgBinding): string {
  const key = String(b.workflow_arg || "").trim();
  if (b.value_source === "static") {
    return `${key}←static:${truncate(b.static_value || "", 20)}`;
  }
  return `${key}←{{${b.variable}}}`;
}

interface PromptVariableProps {
  variable: GenerationVariable;
  index: number;
  workflows: Workflow[];
  disableUp: boolean;
  disableDown: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export function PromptVariable({
  variable,
  index,
  workflows,
  disableUp,
  disableDown,
  onEdit,
  onRemove,
  onMoveUp,
  onMoveDown,
}: PromptVariableProps) {
  const name = variable.name.trim();
  const isComputed = variable.source === "computed";

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-md border border-border bg-background px-3 py-2"
      )}
    >
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-muted-foreground font-mono w-5 tabular-nums">{index + 1}</span>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp} disabled={disableUp} aria-label="Move up">
          <ArrowUpIcon className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown} disabled={disableDown} aria-label="Move down">
          <ArrowDownIcon className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-foreground">{`{{${name}}}`}</code>
          <Badge
            variant="secondary"
            className={cn(
              "text-[10px] font-gc-med px-1.5 py-0 h-5",
              isComputed ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100" : "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
            )}
          >
            {isComputed ? "Computed" : "Seed"}
          </Badge>
        </div>
        {variable.description.trim() ? (
          <p className="text-xs text-muted-foreground leading-snug">{truncate(variable.description, 120)}</p>
        ) : (
          <p className="text-xs text-muted-foreground/60 italic">No description</p>
        )}
        {variable.example.trim() ? (
          <p className="text-[11px] text-muted-foreground/90 leading-snug">
            <span className="font-gc-med text-foreground/70">Example: </span>
            {truncate(variable.example, 100)}
          </p>
        ) : null}
        {isComputed && (
          <p className="text-[11px] text-muted-foreground font-mono leading-snug">
            {variable.workflow_ref
              ? workflowDisplayName(workflows, variable.workflow_ref)
              : "No workflow"}
            {(variable.arg_bindings || []).length > 0
              ? ` · ${(variable.arg_bindings || []).map((b) => summarizeArgBinding(b)).join(", ")}`
              : ""}
            {variable.value_path ? ` · ${variable.value_path}` : ""}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0 self-end sm:self-center">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} aria-label="Edit variable">
          <PencilIcon className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={onRemove} aria-label="Remove variable">
          <Trash2Icon className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
